"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

export default function ModernDialog({
    isOpen,
    onClose,
    title,
    description,
    children,
    disableClose = false,
}) {
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setMounted(true), 0);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (isOpen) {
            const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
            document.body.style.overflow = "hidden";
            document.body.style.paddingRight = `${scrollbarWidth}px`;

            // Adjust the absolute dialog positioning to offset the padding shift for perfect centering
            document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
        } else {
            document.body.style.overflow = "";
            document.body.style.paddingRight = "";
            document.documentElement.style.removeProperty('--scrollbar-width');
        }
        return () => {
            document.body.style.overflow = "";
            document.body.style.paddingRight = "";
            document.documentElement.style.removeProperty('--scrollbar-width');
        };
    }, [isOpen]);

    if (!isOpen || !mounted || typeof document === "undefined") return null;

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in duration-200" style={{ paddingRight: 'var(--scrollbar-width)' }}>
            {/* Backdrop click off */}
            <div
                className="absolute inset-0"
                onClick={disableClose ? undefined : onClose}
            />

            <div className="relative w-full max-w-md scale-100 rounded-2xl bg-white p-6 shadow-2xl animate-in zoom-in-95 duration-200 md:p-8 m-4">
                <button
                    onClick={onClose}
                    disabled={disableClose}
                    className={`absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-zinc-100 text-zinc-500 transition ${disableClose ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:bg-zinc-200 hover:text-zinc-900"}`}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="2"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>
                {title && (
                    <h2 className="font-cormorant text-2xl font-semibold text-zinc-900">
                        {title}
                    </h2>
                )}
                {description && (
                    <p className="mt-2 text-sm text-zinc-600 leading-relaxed">
                        {description}
                    </p>
                )}
                <div className="mt-6">{children}</div>
            </div>
        </div>,
        document.body
    );
}
